import _ from "lodash";
import { isSystemAdmin } from "./roleChecks";

const modulePaths = {
  "/signin": "*",
  "/signup": "*",
  "/welcome": "*",
  "/home": "cc_home",
  "/unauthorized": "*",
  "/no-access": "*",
  "/forgot": "*",
  "/google": "*",
  "/google/callback": "*",
  "/microsoft": "*",
  "/microsoft/callback": "*",
  "/patient-assignment": "dispatch",
  "/schedules": "scheduler_settings",
  "/schedules/careplan-upload": "scheduler_settings.careplan_upload",
  "/schedules/run-scheduler": "scheduler_settings.run_scheduler",
  "/schedules/configure": "scheduler_settings.data_and_settings",
  "/schedules/configure/providers": "scheduler_settings.data_and_settings.provider_settings",
  "/schedules/configure/visit-rules": "scheduler_settings.data_and_settings.visit_rules",
  "/schedules/configure/visit-types": "scheduler_settings.data_and_settings.visit_types",
  "/schedules/configure/care-programs": "scheduler_settings.data_and_settings.care_programs",
  "/schedules/configure/program-frequency": "scheduler_settings.data_and_settings.program_frequency",
  "/schedules/configure/appointment-type-criteria": "scheduler_settings.data_and_settings.appointment_type_criteria",
  "/calendar-view": "calendar_view",
  "/user-management": "*",
  "/role-management": "*",
  "/account": "*",
  "/billing": "*",
  "/plans": "*",
  "/patient-panel-visualizer": "patient_panel_visualizer",
  "/data-management": "data_management",
  "/data-management/patients": "data_management.patient",
  "/data-management/patients/:id": "data_management.patient",
  "/data-management/providers": "data_management.provider",
  "/data-management/providers/:id": "data_management.provider",
  "/data-management/members": "data_management.members",
  "/data-management/communities": "data_management.communities",
  "/data-management/communities/:id": "data_management.communities",
  "/data-management/caretags": "data_management.caretags",
  "/data-management/zones": "data_management.zones",
  "/data-management/referral-sources": "data_management.referral_sources",
  "/chat": "messenger",
  "/reports": "analytics_reporting",
  "/reports/patient-visit-report": "analytics_reporting.standard_reports.patient_visit_report",
  "/reports/provider-activity-report": "analytics_reporting.standard_reports.provider_activity_report",
  "/reports/revenue-report": "analytics_reporting.standard_reports.revenue_report",
  "/reports/appointment-cancellation-report": "analytics_reporting.standard_reports.appointment_cancellation_report",
  "/reports/overdue-patients-report": "analytics_reporting.standard_reports.overdue_patients_report",
  "/reports/new-patients-report": "analytics_reporting.standard_reports.new_patients_report",
  "/reports/no-show-report": "analytics_reporting.standard_reports.no_show_report",
  "/reports/provider-daily-mileage-report": "analytics_reporting.standard_reports.provider_daily_mileage_report",
  "/reports/patient-zipcode-report": "analytics_reporting.standard_reports.patient_zip_coe_report",
  "/reports/report-builder": "analytics_reporting.report_builder",
  "/analytics": "analytics",
  "/analytics/pivot": "analytics.pivot",
  "/analytics/dashboard": "analytics.dashboard",
  "/reports/shared-reports/:id": "*",
  "/dashboards/shared-dashboards/:id": "*"
}


const pathPermissions = {
  "/signin": "*",
  "/signup": "*",
  "/welcome": "*",
  "/home": "cc_home.view_cc_home",
  "/unauthorized": "*",
  "/no-access": "*",
  "/forgot": "*",
  "/google": "*",
  "/google/callback": "*",
  "/microsoft": "*",
  "/microsoft/callback": "*",
  "/patient-assignment": "dispatch.view_dispatch",
  "/schedules": "scheduler_settings.view_scheduler_settings",
  "/schedules/careplan-upload": "scheduler_settings.careplan_upload",
  "/schedules/run-scheduler": "scheduler_settings.run_scheduler",
  "/schedules/configure": "scheduler_settings.data_and_settings",
  "/schedules/configure/providers": "provider_settings.view_provider_settings",
  "/schedules/configure/visit-rules": "visit_rules.view_visit_rules",
  "/schedules/configure/visit-types": "visit_types.view_visit_types",
  "/schedules/configure/care-programs": "care_programs.view_care_programs",
  "/schedules/configure/program-frequency": "program_frequency.view_program_frequency",
  "/schedules/configure/appointment-type-criteria": "appointment_type_criteria.view_appointment_type_criteria",
  "/calendar-view": "calendar_view.view_calendar_view",
  "/user-management": "*",
  "/role-management": "*",
  "/account": "*",
  "/billing": "*",
  "/plans": "*",
  "/patient-panel-visualizer": "patient_panel_visualizer.view_patient_panel_visualizer",
  "/data-management": "data_management.view_data_management",
  "/data-management/patients": "patients_dm.view_patients",
  "/data-management/patients/:id": "patients_dm.view_specific_patient",
  "/data-management/providers": "providers_dm.view_providers",
  "/data-management/providers/:id": "providers_dm.view_specific_provider",
  "/data-management/members": "members_dm.view_members",
  "/data-management/communities": "communities_dm.view_communities",
  "/data-management/communities/:id": "communities_dm.view_specific_community",
  "/data-management/caretags": "caretags_dm.view_caretags",
  "/data-management/zones": "zones_dm.view_zones",
  "/data-management/referral-sources": "referral_sources_dm.view_referral_sources",
  "/chat": "messenger.*",
  "/reports": "reporting_analytics.*",
  "/reports/patient-visit-report": "standard_reports.patient_visit_report.*",
  "/reports/provider-activity-report": "standard_reports.provider_activity_report.*",
  "/reports/revenue-report": "standard_reports.revenue_report.*",
  "/reports/appointment-cancellation-report": "standard_reports.appointment_cancellation_report.*",
  "/reports/overdue-patients-report": "standard_reports.overdue_patients_report.*",
  "/reports/new-patients-report": "standard_reports.new_patients_report.*",
  "/reports/no-show-report": "standard_reports.no_show_report.*",
  "/reports/provider-daily-mileage-report": "standard_reports.provider_daily_mileage_report.*",
  "/reports/patient-zipcode-report": "standard_reports.patient_zip_coe_report.*",
  "/reports/report-builder": "reporting_analytics.report_builder.*",
  "/analytics": "reporting_analytics.*",
  "/analytics/pivot": "reporting_analytics.pivot.*",
  "/analytics/dashboard": "reporting_analytics.dashboard.*",
  "/reports/shared-reports/:id": "*",
  "/dashboards/shared-dashboards/:id": "*"
}

function getPathValue(pathMap, url) {
  // Exact match first
  if (pathMap[url]) return pathMap[url];

  for (const key in pathMap) {
    // Convert path pattern into a regex:
    // - Replace `:param` with a segment matcher ([^/]+)
    // - Allow optional trailing slash
    const pattern = key
      .replace(/:[^/]+/g, "[^/]+")
      .replace(/\/+$/, ""); // remove trailing slashes

    const regex = new RegExp(`^${pattern}/?$`);
    if (regex.test(url.replace(/\/+$/, ""))) {
      return pathMap[key];
    }
  }

  return undefined;
}

export function checkModuleAccess(path, modules, user = null) {
  const moduleName = getPathValue(modulePaths, path);
  
  // Special handling for system admin only routes
  if (path === "/user-management" || path === "/role-management") {
    return user ? isSystemAdmin(user) : false;
  }
  
  if (moduleName === "*") return true;
  return _.has(modules, moduleName);
}


export function checkPermission(path, permissions, user = null) {
  const permission = getPathValue(pathPermissions, path);
  
  // Special handling for system admin only routes
  if (path === "/user-management" || path === "/role-management") {
    return user ? isSystemAdmin(user) : false;
  }
  
  if (permission === "*") return true;
  return permissions.includes(permission);
}


export const hasAccess = (path, modules, permissions, user = null) => {
  console.log("Has module access", checkModuleAccess(path, modules, user), "Has permission", checkPermission(path, permissions, user));
  return checkModuleAccess(path, modules, user) && checkPermission(path, permissions, user);
}


export function checkPermissionGrant(permission, permissions) {
  if (permission === "*") return true;
  return permissions.includes(permission);
}